import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { authCheck, authGoogleLogin, authLogin, authLogout } from 'api/auth';
import { clearAuthCache, isPersistentAuthCache, readCachedAuth, saveAuthCache } from 'utils/authSession';
import { preloadGoogleIdentityScript } from 'utils/loadGoogleIdentity';
import { GOOGLE_OAUTH_CLIENT_ID } from 'config/env';

const AuthContext = createContext(null);

async function verifySessionAfterLogin() {
  const json = await authCheck();
  if (!json?.authenticated) {
    throw new Error(
      'Login succeeded but the session cookie was not saved. Use the same site URL for the app and API, or check browser cookie settings.'
    );
  }
  return json;
}

export function AuthProvider({ children }) {
  const cachedUser = readCachedAuth();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(!!cachedUser);
  const [user, setUser] = useState(cachedUser);
  const rememberRef = useRef(isPersistentAuthCache());

  const applyAuth = useCallback((nextUser, persistent = rememberRef.current) => {
    if (nextUser) {
      setAuthenticated(true);
      setUser(nextUser);
      saveAuthCache(nextUser, persistent);
      rememberRef.current = persistent;
    } else {
      setAuthenticated(false);
      setUser(null);
      clearAuthCache();
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const json = await authCheck();
      if (json?.authenticated && json?.user?.is_blacklisted) {
        applyAuth(null);
      } else if (json?.authenticated) {
        applyAuth(json.user || null, isPersistentAuthCache() || rememberRef.current);
      } else {
        applyAuth(null);
      }
    } catch {
      // Network / server error: keep cached session so user is not bounced to login repeatedly.
      const fallback = readCachedAuth();
      if (fallback?.is_blacklisted) {
        setAuthenticated(false);
        setUser(null);
      } else if (fallback) {
        setAuthenticated(true);
        setUser(fallback);
      } else {
        setAuthenticated(false);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [applyAuth]);

  useEffect(() => {
    if (GOOGLE_OAUTH_CLIENT_ID) {
      preloadGoogleIdentityScript();
    }
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (credentials, { remember = false } = {}) => {
      const json = await authLogin({ ...credentials, remember });
      const verified = await verifySessionAfterLogin();
      applyAuth(verified.user || json.user || null, remember);
      return { ...json, user: verified.user || json.user };
    },
    [applyAuth]
  );

  const loginWithGoogle = useCallback(
    async (idToken, { remember = true } = {}) => {
      const json = await authGoogleLogin({ idToken, remember });
      const verified = await verifySessionAfterLogin();
      const user = verified.user || json.user || null;
      applyAuth(user, remember);
      return {
        ...json,
        user,
        needs_profile_completion: Boolean(json?.needs_profile_completion) && Boolean(json?.is_new_user)
      };
    },
    [applyAuth]
  );

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } finally {
      applyAuth(null);
    }
  }, [applyAuth]);

  const setUserAndCache = useCallback(
    (nextUser) => {
      if (nextUser) {
        applyAuth(nextUser, rememberRef.current);
      } else {
        applyAuth(null);
      }
    },
    [applyAuth]
  );

  const value = useMemo(
    () => ({
      loading,
      authenticated,
      user,
      isAdmin: String(user?.role || '')
        .trim()
        .toLowerCase() === 'admin',
      refresh,
      login,
      loginWithGoogle,
      setUser: setUserAndCache,
      logout
    }),
    [loading, authenticated, user, refresh, login, loginWithGoogle, setUserAndCache, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
